import { Constants } from "@eagerdog/constants";
import { IScore } from "@eagerdog/interfaces";

// Each fault adds this many seconds to the dog's effective time for placement ordering.
const SDG_FAULT_PENALTY_SECONDS = 5;

function parseSDGSearchTime(timeStr: string): number {
  const parts = timeStr.split(":").map((p) => parseInt(p, 10) || 0);
  const mm = parts[0] ?? 0;
  const ss = parts[1] ?? 0;
  const tt = parts[2] ?? 0;
  return mm * 6000 + ss * 100 + tt; // units: 1/100 second
}

function formatSDGTime(hundredths: number): string {
  const mm = Math.floor(hundredths / 6000);
  const remaining = hundredths % 6000;
  const ss = Math.floor(remaining / 100);
  const tt = remaining % 100;
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}:${String(
    tt
  ).padStart(2, "0")}`;
}

export function getSDGEffectiveTime(sdg: {
  fault_count?: number;
  search_time?: string;
}): number {
  if (!sdg.search_time) return Number.MAX_SAFE_INTEGER;
  const rawTime = parseSDGSearchTime(sdg.search_time);
  const faultPenalty = (sdg.fault_count ?? 0) * SDG_FAULT_PENALTY_SECONDS * 100;
  return rawTime + faultPenalty;
}

export function formatSDGEffectiveTime(sdg: {
  fault_count?: number;
  search_time?: string;
}): string {
  const effective = getSDGEffectiveTime(sdg);
  if (effective === Number.MAX_SAFE_INTEGER) return "";
  return formatSDGTime(effective);
}

export const abiScentDogGamesGetResultLabel = (score: IScore): string => {
  return score.scent_dog_games?.result_type || "No Result";
};

export const abiScentDogGamesSort = (a: IScore, b: IScore): number => {
  const { dog_class: dcA } = a;
  const { dog_class: dcB } = b;

  const sectionA = dcA?.section || "";
  const sectionB = dcB?.section || "";
  if (sectionA < sectionB) return -1;
  if (sectionA > sectionB) return 1;

  const sdgA = a.scent_dog_games;
  const sdgB = b.scent_dog_games;

  if (!sdgA) return !sdgB ? 0 : 1;
  if (!sdgB) return -1;

  if (sdgA.result_type === Constants.score_result_type.qualified) {
    if (sdgB.result_type === Constants.score_result_type.qualified) {
      return getSDGEffectiveTime(sdgA) - getSDGEffectiveTime(sdgB);
    }
    return -1;
  }

  if (sdgB.result_type === Constants.score_result_type.qualified) return 1;

  if (sdgA.result_type === Constants.score_result_type.not_qualified) {
    if (sdgB.result_type === Constants.score_result_type.not_qualified) return 0;
    return -1;
  }

  return 1;
};

export const abiScentDogGamesGetPlacement = (
  score: IScore,
  eligibleScores: IScore[]
): { place: string; num_dogs: number } => {
  const { show_element, level, section } = score.dog_class ?? {};

  const elementScores = eligibleScores
    .filter(
      (s) =>
        s.dog_class?.show_element === show_element &&
        s.dog_class?.level === level &&
        (section ? s.dog_class?.section === section : true)
    )
    .sort((a, b) => abiScentDogGamesSort(a, b));

  const placementMap = new Map<number, number>();
  elementScores.forEach((s, index) => {
    if (
      s.scent_dog_games?.result_type === Constants.score_result_type.qualified
    ) {
      let place = index + 1;
      if (index > 0) {
        const prev = elementScores[index - 1];
        if (
          prev.scent_dog_games?.result_type ===
            Constants.score_result_type.qualified &&
          getSDGEffectiveTime(prev.scent_dog_games) ===
            getSDGEffectiveTime(s.scent_dog_games!)
        ) {
          place = placementMap.get(index - 1)!;
        }
      }
      placementMap.set(index, place);
    }
  });

  const index = elementScores.findIndex(
    (s) => JSON.stringify(s.dog_id) === JSON.stringify(score.dog_id)
  );
  const place = placementMap.get(index);
  const num_dogs = elementScores.length;

  return {
    place: place ? `${place}/${num_dogs}` : "",
    num_dogs,
  };
};
